import { useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../lib/api';
import PageHeader from '../components/Layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Package, Truck, CheckCircle, Clock, MagnifyingGlass,
  ArrowRight, WarningCircle, XCircle, Eye, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import CustomerDrillDownDialog from '../components/CustomerDrillDownDialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

const STATUS_CONFIG = {
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Package },
  placed: { label: 'Placed', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Package },
  in_transit: { label: 'In Transit', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck },
  fulfilled: { label: 'Fulfilled', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  invoiced: { label: 'Invoiced', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  partially_invoiced: { label: 'Partially Invoiced', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: CheckCircle },
  void: { label: 'Void', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
};

const NEXT_STATUS = {
  sent: [{ value: 'placed', label: 'Mark as Placed' }, { value: 'cancelled', label: 'Cancel' }],
  placed: [{ value: 'in_transit', label: 'Mark In Transit' }, { value: 'cancelled', label: 'Cancel' }],
  in_transit: [{ value: 'fulfilled', label: 'Mark Fulfilled' }, { value: 'cancelled', label: 'Cancel' }],
};

function SOStatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return <Badge variant="outline" className={`text-xs ${cfg.color}`} data-testid={`so-status-${status}`}>{cfg.label}</Badge>;
}

export default function OrderManagementPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusSummary, setStatusSummary] = useState({});
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // YYYY-MM
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [drill, setDrill] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 400); return () => clearTimeout(t); }, [search]);

  // When monthFilter changes, sync from/to dates to that month
  useEffect(() => {
    if (!monthFilter) return;
    const [y, m] = monthFilter.split('-');
    if (!y || !m) return;
    const last = new Date(parseInt(y), parseInt(m), 0).getDate();
    setFromDate(`${y}-${m}-01`);
    setToDate(`${y}-${m}-${String(last).padStart(2, '0')}`);
  }, [monthFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await billingAPI.adminGetSalesOrders(params);
      setOrders(data.sales_orders || []);
      setTotal(data.total || 0);
      setStatusSummary(data.status_summary || {});
    } catch (e) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [page, statusFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch, fromDate, toDate]);

  const handleStatusChange = async (soId, newStatus) => {
    setUpdating(true);
    try {
      await billingAPI.adminUpdateSOStatus(soId, { status: newStatus, note: statusNote });
      toast.success(`Order updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setStatusNote('');
      setSelectedOrder(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setUpdating(false); }
  };

  const clearFilters = () => {
    setStatusFilter(''); setFromDate(''); setToDate(''); setMonthFilter(''); setSearch('');
  };

  const pending = (statusSummary.sent || 0) + (statusSummary.placed || 0) + (statusSummary.confirmed || 0);
  const inTransit = statusSummary.in_transit || 0;
  const fulfilled = statusSummary.fulfilled || 0;
  const invoiced = statusSummary.invoiced || 0;
  const voidCount = statusSummary.void || 0;

  return (
    <div className="space-y-6" data-testid="order-management-page">
      <PageHeader title="Order Management" subtitle="Track and manage clinic sales orders across the fulfillment pipeline" />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Confirmed" value={statusSummary.confirmed || 0} color="amber" icon={Package} onClick={() => setStatusFilter('confirmed')} active={statusFilter === 'confirmed'} />
        <StatCard label="Invoiced" value={invoiced} color="emerald" icon={CheckCircle} onClick={() => setStatusFilter('invoiced')} active={statusFilter === 'invoiced'} />
        <StatCard label="Void" value={voidCount} color="red" icon={XCircle} onClick={() => setStatusFilter('void')} active={statusFilter === 'void'} />
        <StatCard label="Sent" value={statusSummary.sent || 0} color="blue" icon={Clock} onClick={() => setStatusFilter('sent')} active={statusFilter === 'sent'} />
        <StatCard label="All" value={total} color="gray" icon={Package} onClick={() => setStatusFilter('')} active={!statusFilter} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search SO number, customer..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl" data-testid="order-search" />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 rounded-xl" data-testid="order-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
            <SelectItem value="partially_invoiced">Partially Invoiced</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Month</label>
          <Input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            className="w-36 rounded-xl" data-testid="order-month-filter" />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">From</label>
          <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setMonthFilter(''); }}
            className="w-40 rounded-xl" data-testid="order-from-date" />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">To</label>
          <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setMonthFilter(''); }}
            className="w-40 rounded-xl" data-testid="order-to-date" />
        </div>
        {(statusFilter || fromDate || toDate || monthFilter || search) && (
          <Button variant="outline" size="sm" onClick={clearFilters} data-testid="order-clear-filters" className="rounded-xl">
            Clear
          </Button>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">SO Number</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Customer</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Clinic</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Items</th>
                <th className="px-4 py-3 text-right font-semibold text-[#0B1B3D]">Amount</th>
                <th className="px-4 py-3 text-center font-semibold text-[#0B1B3D]">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-[#0B1B3D]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No orders found</td></tr>
              ) : orders.map(so => (
                <tr key={so.id} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`order-row-${so.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0B1B3D]">{so.so_number}</p>
                    <p className="text-xs text-gray-400">{so.date}</p>
                  </td>
                  <td className="px-4 py-3">
                    {(so.customer?.name || so.customer_name) ? (
                      <button onClick={() => setDrill({ id: so.customer_id || so.customer?.id, name: so.customer?.name || so.customer_name, phone: so.customer?.phone || so.customer_phone })} className="font-medium text-[#0B1B3D] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`order-customer-${so.id}`}>{so.customer?.name || so.customer_name}</button>
                    ) : <p className="font-medium">-</p>}
                    <p className="text-xs text-gray-400">{so.customer?.phone || so.customer_phone}</p>
                  </td>
                  <td className="px-4 py-3 text-sm">{so.clinic_name}<br /><span className="text-xs text-gray-400">{so.clinic_code}</span></td>
                  <td className="px-4 py-3 text-sm">{so.line_items?.length || 0} items</td>
                  <td className="px-4 py-3 text-right font-semibold">₹{fmt(so.grand_total)}</td>
                  <td className="px-4 py-3 text-center"><SOStatusBadge status={so.status} /></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSelectedOrder(so)} data-testid={`view-order-${so.id}`}>
                        <Eye size={14} className="text-gray-600" />
                      </Button>
                      {NEXT_STATUS[so.status] && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600"
                          onClick={() => setSelectedOrder(so)} data-testid={`manage-order-${so.id}`}>
                          <ArrowRight size={14} className="mr-0.5" /> Update
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 50 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl"><CaretLeft size={14} /></Button>
            <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl"><CaretRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog order={selectedOrder} onClose={() => { setSelectedOrder(null); setStatusNote(''); }}
          onStatusChange={handleStatusChange} statusNote={statusNote} setStatusNote={setStatusNote} updating={updating} />
      )}
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} customerPhone={drill?.phone || ''} />
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, onClick, active }) {
  const colors = {
    amber: 'border-amber-200 text-amber-600', blue: 'border-blue-200 text-blue-600',
    purple: 'border-purple-200 text-purple-600', green: 'border-green-200 text-green-600',
    gray: 'border-gray-200 text-gray-600',
  };
  return (
    <Card className={`rounded-2xl cursor-pointer transition-all ${active ? 'ring-2 ring-[#0B1B3D]' : ''} ${colors[color]}`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1"><Icon size={16} /><span className="text-xs text-gray-500">{label}</span></div>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

function OrderDetailDialog({ order, onClose, onStatusChange, statusNote, setStatusNote, updating }) {
  const so = order;
  const nextActions = NEXT_STATUS[so.status] || [];
  const history = so.status_history || [];

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="text-xl">{so.so_number}</span>
            <SOStatusBadge status={so.status} />
          </DialogTitle>
        </DialogHeader>

        {/* Order Info */}
        <div className="grid grid-cols-2 gap-4 text-sm mt-2">
          <div>
            <p className="text-xs text-gray-400 mb-1">Customer</p>
            <p className="font-medium">{so.customer?.name}</p>
            <p className="text-gray-500">{so.customer?.phone}</p>
            <p className="text-gray-500">{so.customer?.address}</p>
            <p className="text-gray-500">{so.customer?.state}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Clinic</p>
            <p className="font-medium">{so.clinic_name}</p>
            <p className="text-gray-500">Code: {so.clinic_code}</p>
            <p className="text-xs text-gray-400 mt-2">Date: {so.date}</p>
            {so.expected_delivery_date && <p className="text-xs text-gray-400">Expected: {so.expected_delivery_date}</p>}
          </div>
        </div>

        {/* Line Items */}
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Items</h4>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Rate</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {so.line_items?.map((li, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-3 py-2"><p className="font-medium">{li.name}</p>{li.hsn_sac && <p className="text-xs text-gray-400">HSN: {li.hsn_sac}</p>}</td>
                    <td className="px-3 py-2 text-center">{li.quantity}</td>
                    <td className="px-3 py-2 text-right">₹{fmt(li.rate)}</td>
                    <td className="px-3 py-2 text-right font-medium">₹{fmt(li.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-2">
            <div className="text-right text-sm space-y-1">
              <div className="flex justify-between gap-8"><span className="text-gray-500">Subtotal</span><span>₹{fmt(so.subtotal)}</span></div>
              {so.total_tax > 0 && <div className="flex justify-between gap-8"><span className="text-gray-500">Tax</span><span>₹{fmt(so.total_tax)}</span></div>}
              <div className="flex justify-between gap-8 font-semibold text-[#0B1B3D] border-t pt-1"><span>Grand Total</span><span>₹{fmt(so.grand_total)}</span></div>
            </div>
          </div>
        </div>

        {/* Status History */}
        {history.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Status History</h4>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <SOStatusBadge status={h.status} />
                  <span className="text-xs text-gray-400">{new Date(h.timestamp).toLocaleString('en-IN')}</span>
                  {h.note && <span className="text-xs text-gray-500">— {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {so.converted_to_invoice_number && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm">
            Converted to Invoice: <strong>{so.converted_to_invoice_number}</strong>
          </div>
        )}

        {/* Status Update Actions */}
        {nextActions.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Update Status</h4>
            <Input placeholder="Add a note (optional)" value={statusNote} onChange={e => setStatusNote(e.target.value)}
              className="rounded-xl mb-3" data-testid="status-note-input" />
            <div className="flex flex-wrap gap-2">
              {nextActions.map(action => (
                <Button key={action.value} variant={action.value === 'cancelled' ? 'outline' : 'default'}
                  size="sm" disabled={updating}
                  className={`rounded-xl ${action.value === 'cancelled' ? 'text-red-600 border-red-200' : 'bg-[#0B1B3D] hover:bg-[#16274D] text-white'}`}
                  onClick={() => onStatusChange(so.id, action.value)}
                  data-testid={`status-btn-${action.value}`}>
                  <ArrowRight size={14} className="mr-1" /> {action.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
